import { useId, useState } from "react";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatHoursMinutes, parseMinutes } from "@/lib/duration";
import { DurationField } from "./DurationField";

export interface TimeLogValues {
	minutes: number;
	date: string;
	note: string;
}

function localToday(): string {
	return format(new Date(), "yyyy-MM-dd");
}

const fieldLabel = "text-xs font-medium text-muted";

export function TimeLogForm({
	initial,
	submitLabel,
	onSubmit,
	onCancel,
}: {
	initial?: TimeLogValues;
	submitLabel: string;
	onSubmit: (values: TimeLogValues) => Promise<void>;
	onCancel: () => void;
}) {
	const id = useId();
	const [text, setText] = useState(
		initial ? formatHoursMinutes(initial.minutes) : "",
	);
	const [date, setDate] = useState(initial?.date ?? localToday());
	const [note, setNote] = useState(initial?.note ?? "");
	const [saving, setSaving] = useState(false);

	const minutes = parseMinutes(text);
	const canSubmit = !!minutes && minutes > 0 && !!date && !saving;

	async function submit(e?: React.FormEvent) {
		e?.preventDefault();
		if (!canSubmit || !minutes) return;
		setSaving(true);
		try {
			await onSubmit({ minutes, date, note: note.trim() });
		} catch (err) {
			toast.error("Couldn't save time log", {
				description: (err as Error).message || "Please try again.",
			});
		} finally {
			setSaving(false);
		}
	}

	return (
		<form onSubmit={submit} className="flex flex-col gap-3">
			<div className="flex flex-col gap-1.5">
				<label htmlFor={`${id}-dur`} className={fieldLabel}>
					Duration
				</label>
				<DurationField
					id={`${id}-dur`}
					autoFocus
					text={text}
					onTextChange={setText}
					disabled={saving}
				/>
			</div>

			<div className="flex flex-col gap-1.5">
				<label htmlFor={`${id}-date`} className={fieldLabel}>
					Date
				</label>
				<Input
					id={`${id}-date`}
					type="date"
					value={date}
					max={localToday()}
					disabled={saving}
					onChange={(e) => setDate(e.target.value)}
					className="h-9"
				/>
			</div>

			<div className="flex flex-col gap-1.5">
				<label htmlFor={`${id}-note`} className={fieldLabel}>
					Note <span className="font-normal">(optional)</span>
				</label>
				<textarea
					id={`${id}-note`}
					value={note}
					rows={2}
					placeholder="What did you work on?"
					disabled={saving}
					onChange={(e) => setNote(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
					}}
					className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-primary"
				/>
			</div>

			<div className="flex items-center justify-end gap-2">
				<Button
					type="button"
					size="sm"
					variant="ghost"
					disabled={saving}
					onClick={onCancel}
				>
					Cancel
				</Button>
				<Button type="submit" size="sm" disabled={!canSubmit}>
					{saving && <Loader2 className="h-3 w-3 animate-spin" />}
					{submitLabel}
				</Button>
			</div>
		</form>
	);
}
